import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import Accordion from "../../../../../components/Accordion";
import Confirm from "../../../../../components/Confirm";
import Table, { ColumnDefinitionType } from "../../../../../components/Table";
import { DocumentoEquipamiento, TipoDocumentoOpcion } from "../../../../../types/DocumentoEquipamiento";
import ModalTipoDocumento from "./ModalTipoDocumento";

type SeccionTipoDocumentoProps = {
    documentos: DocumentoEquipamiento[];
    setDocumentos: (value: DocumentoEquipamiento[]) => void;
    // Tipos de documento activos del sistema (combo del modal).
    tiposActivos: TipoDocumentoOpcion[];
    view: boolean;
}

const SeccionTipoDocumento = ({ documentos, setDocumentos, tiposActivos, view }: SeccionTipoDocumentoProps) => {
    const [modalAbierto, setModalAbierto] = useState<boolean>(false);
    const [enEdicion, setEnEdicion] = useState<DocumentoEquipamiento | null>(null);
    const [aQuitar, setAQuitar] = useState<DocumentoEquipamiento | null>(null);

    // El combo ofrece los tipos activos que aún no están en la grilla, más el que se está editando.
    const tiposDisponibles = useMemo(() => {
        const usados = new Set(documentos.map((d) => d.tipoDocumentoId));
        const disponibles = tiposActivos.filter((t) => !usados.has(t.tipoDocumentoId));
        if (enEdicion && enEdicion.tipoDocumentoId !== null) {
            const actual = { tipoDocumentoId: enEdicion.tipoDocumentoId, tipoDocumentoNombre: enEdicion.tipoDocumentoNombre };
            return [actual, ...disponibles];
        }
        return disponibles;
    }, [documentos, tiposActivos, enEdicion]);

    const abrirAgregar = (e: React.MouseEvent) => {
        e.preventDefault();
        setEnEdicion(null);
        setModalAbierto(true);
    }

    const abrirEditar = (documento: DocumentoEquipamiento) => (e: React.MouseEvent) => {
        e.preventDefault();
        setEnEdicion(documento);
        setModalAbierto(true);
    }

    const aceptar = (documento: DocumentoEquipamiento) => {
        if (enEdicion) {
            setDocumentos(documentos.map((d) => d.tipoDocumentoId === enEdicion.tipoDocumentoId ? documento : d));
        } else {
            setDocumentos([...documentos, documento]);
        }
        setModalAbierto(false);
    }

    const quitar = () => {
        if (aQuitar) {
            setDocumentos(documentos.filter((d) => d.tipoDocumentoId !== aQuitar.tipoDocumentoId));
        }
        setAQuitar(null);
    }

    const columns: ColumnDefinitionType<DocumentoEquipamiento, keyof DocumentoEquipamiento>[] = [
        { key: 'tipoDocumentoNombre', header: 'Tipo de Documento' },
        { key: 'obligatorio', header: 'Obligatorio', cell: (d: DocumentoEquipamiento) => <>{d.obligatorio ? 'Sí' : 'No'}</> },
        { key: 'renovable', header: 'Renovable', cell: (d: DocumentoEquipamiento) => <>{d.renovable ? 'Sí' : 'No'}</> },
        { key: 'fechaEditable', header: 'Fecha Editable', cell: (d: DocumentoEquipamiento) => <>{d.fechaEditable ? 'Sí' : 'No'}</> },
        { key: 'periodo', header: 'Periodicidad (Días)', cell: (d: DocumentoEquipamiento) => <>{d.periodo > 0 ? d.periodo : ''}</> },
    ];
    if (!view) {
        columns.push({
            key: 'tipoDocumentoId',
            header: 'Acciones',
            cell: (d: DocumentoEquipamiento) => (
                <>
                    <Link to="#" title="Editar" onClick={abrirEditar(d)}>
                        <i className="fa fa-pencil text-custom me-2" aria-hidden="true"></i>
                    </Link>
                    <Link to="#" title="Quitar" onClick={(e) => { e.preventDefault(); setAQuitar(d); }}>
                        <i className="fa fa-trash-o mr-2 text-danger" aria-hidden="true"></i>
                    </Link>
                </>
            )
        });
    }

    return (
        <>
            <Confirm isOpen={aQuitar !== null} toggle={() => setAQuitar(null)} title='Eliminar' yesFn={quitar}>
                <div className="form-horizontal">
                    <div className="row">
                        <p>Está seguro que desea eliminar el tipo de documento {aQuitar?.tipoDocumentoNombre}?</p>
                    </div>
                </div>
            </Confirm>

            <ModalTipoDocumento
                isOpen={modalAbierto}
                documentoInicial={enEdicion}
                tiposDisponibles={tiposDisponibles}
                onAceptar={aceptar}
                onCerrar={() => setModalAbierto(false)}
            />

            <Accordion id={'documentos'} title="Tipos de Documento">
                <div className="card">
                    <div className="card-body">
                        <div className="row">
                            <div className="col-lg-12">
                                <div className="ibox float-e-margins">
                                    <div className="ibox-title">
                                        <h4 className="m-t-0 header-title">
                                            <i className="fa fa-list-ul" aria-hidden="true" style={{ marginRight: "5px" }}></i>
                                            Listado de Tipos de Documento
                                            {!view &&
                                                <Link to="#" title="Agregar" className="btn-custom btn-rounded waves-effect waves-light pull-right btn-sm"
                                                    style={{ marginTop: '-8px' }} onClick={abrirAgregar}>
                                                    <i className="fa fa-plus margin-right-10"></i> Agregar
                                                </Link>
                                            }
                                        </h4>
                                    </div>
                                    <div className="table-responsive">
                                        <br />
                                        <Table data={documentos} columns={columns}></Table>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </Accordion>
        </>
    )
}

export default SeccionTipoDocumento;
